import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  type AgentDto,
  type CampaignDto,
  useCompanyControllerGetCompanyQuery,
  useCampaignSipTrunkControllerGetSipTrunkQuery,
} from '@shared/services/apiService';
import { hasCampaignDialTargets } from '@features/Admin/Campaign/utils/campaign-validation.utils';
import {
  getRemovedAgentSettings,
  getRemovedSettingsActionMessage,
} from '@shared/utils/removedAgentSettings';

/** Inspects saved references only. Opening this dialog never creates a call or operation. */
export const AgentConfigurationChecks = ({
  campaign,
  agents,
  agentsUnavailable = false,
}: {
  campaign: CampaignDto;
  agents: AgentDto[];
  agentsUnavailable?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const company = useCompanyControllerGetCompanyQuery(
    { id: campaign.companyId },
    { skip: !open, refetchOnMountOrArgChange: true },
  );
  const settings = company.currentData;
  const sip = useCampaignSipTrunkControllerGetSipTrunkQuery(
    { id: campaign.id },
    { skip: !open || campaign.type !== 'inbound' },
  );
  const settingsKnown =
    !company.isFetching &&
    !company.isError &&
    Array.isArray(settings?.voices) &&
    Array.isArray(settings?.transcribers) &&
    Array.isArray(settings?.llmProviders);
  const targetsKnown =
    campaign.customersTotal !== undefined &&
    (campaign.type !== 'inbound' || (!sip.isFetching && !sip.isError && !!sip.currentData));
  const hasTargets = hasCampaignDialTargets({
    campaignType: campaign.type,
    hasCustomers: (campaign.customersTotal ?? 0) > 0,
    hasSipNumbers: !!sip.currentData?.numbers?.length,
  });
  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Check configuration
      </Button>
      {open && (
        <Dialog
          open
          onClose={() => setOpen(false)}
          fullWidth
          maxWidth="md"
          aria-labelledby="agent-configuration-title">
          <DialogTitle id="agent-configuration-title">
            Saved configuration checks · {campaign.title}
          </DialogTitle>
          <DialogContent>
            <Stack gap={2}>
              <Alert severity="info">
                Read only: these checks inspect saved agent references and dial targets. Provider
                connectivity, credentials, available credits and runtime admission are checked
                separately when a call starts.
              </Alert>
              <Alert severity={agentsUnavailable || !agents.length ? 'warning' : 'success'}>
                {agentsUnavailable
                  ? 'The current agent list is unavailable; it has not been checked.'
                  : agents.length
                    ? `${agents.length} saved agent(s) found.`
                    : 'No agents are assigned to this campaign.'}
              </Alert>
              {!settingsKnown && (
                <Alert
                  severity={company.isError ? 'error' : 'info'}
                  action={
                    company.isError ? (
                      <Button onClick={() => void company.refetch()}>Retry settings</Button>
                    ) : undefined
                  }>
                  {company.isError
                    ? 'Company settings could not be loaded. Agent references have not been checked.'
                    : 'Waiting for current company settings; agent references have not been checked.'}
                </Alert>
              )}
              {!agentsUnavailable &&
                settingsKnown &&
                agents.map((agent) => {
                  const missing = [
                    !agent.name.trim() && 'name',
                    !agent.voiceId && 'voice',
                    !agent.transcriberId && 'transcriber',
                    !agent.llmProviderId && 'LLM provider',
                    !agent.llmModelId && !agent.llmCustomModelKey?.trim() && 'LLM model',
                    !agent.languages?.some((language) => language.trim()) && 'language',
                  ].filter(Boolean);
                  const removed = getRemovedSettingsActionMessage(
                    getRemovedAgentSettings(agent, settings),
                    t,
                  );
                  return (
                    <Alert
                      key={agent.id}
                      severity={missing.length || removed ? 'warning' : 'success'}>
                      {agent.name || 'Unnamed agent'}:{' '}
                      {missing.length ? `Missing ${missing.join(', ')}. ` : ''}
                      {removed ||
                        (!missing.length
                          ? 'Saved voice, transcriber and LLM references are configured.'
                          : '')}
                    </Alert>
                  );
                })}
              <Alert severity={!targetsKnown ? 'info' : hasTargets ? 'success' : 'warning'}>
                {!targetsKnown
                  ? 'Dial targets are not fully included in this response. Review the campaign audience and SIP configuration.'
                  : hasTargets
                    ? 'Campaign dial targets are configured.'
                    : 'No campaign dial targets are configured.'}
              </Alert>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Close checks</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
